import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { installedItems } from "@p0security/shared/install/installed";
import { Instructions } from "@p0security/shared/install/types";
import { AzureComponents } from "@p0security/shared/integrations/resources/azure/components";
import { azureIamWriteComponentSetupFor } from "@p0security/shared/integrations/resources/azure/helper";
import { AzureIntegration } from "@p0security/shared/integrations/resources/azure/types";
import { Typography } from "antd";
import { FrontendInstallContext, FrontendInstaller } from "install/types";
import { ReactNode } from "react";

import { appRegistrationPreqMessages } from "./util";

const azureIamWriteInstructionsFor = (
  context: FrontendInstallContext<AzureIntegration>,
  id: string
): Instructions<ReactNode> => {
  const azureApp = installedItems("azure-app", context.config);
  const { clientId, clientServicePrincipalId } = azureApp[SINGLETON_ITEM] ?? {};
  if (!clientId || !clientServicePrincipalId) {
    throw new Error("Azure App Registration is not installed.");
  }
  const { customRole } = azureIamWriteComponentSetupFor(
    id,
    clientServicePrincipalId
  );
  const {
    name: roleName,
    description,
    isCustom,
    actions,
    assignableScope,
    condition,
  } = customRole;
  const transformedActions = JSON.stringify(actions, null, 2);
  const shell = [
    {
      header: "Target the subscription P0 is being installed on",
      command: `az account set --subscription ${id}`,
    },
    {
      header: "Create a P0 Management Role",

      command: `
    ${"" /** TODO: Check if the role already exists */}
      # Create a JSON file for the role definition
  cat > p0-management-role.json << 'EOF'
{
  "Name": "${roleName}",
  "IsCustom": ${isCustom},
  "Description": "${description}",
  "Actions": ${transformedActions},
  "NotActions": [],
  "AssignableScopes": [
        "${assignableScope}"
  ]
  ${"" /*TODO ENG-4935: Prevent self-modification of role assignments */}
}
EOF

# Create the custom role
az role definition create --role-definition p0-management-role.json`,
    },
    {
      header:
        "Create role assignment of service principal to new P0 Management Role in the current subscription",
      command: `az role assignment create --assignee ${clientId} --role "${roleName}" --scope ${assignableScope} --condition "${condition}"`,
    },
  ];
  const iac = [
    {
      header: "Create a P0 Management Role",
      command: `resource "azurerm_role_definition" "p0_azure_custom_role" {
  name       = "${roleName}"
  scope       = "${assignableScope}"
  description = "${description}"

  permissions {
    actions     = ${transformedActions}
    not_actions = []
  }

  assignable_scopes = [
    "${assignableScope}"
  ]
}
    `,
    },
    {
      header:
        "Create role assignment of service principal to new P0 Management Role in the current subscription",
      command: `
resource "azurerm_role_assignment" "p0_role_assignment" {
  depends_on         = [azurerm_role_definition.p0_azure_custom_role]
  scope              = "${assignableScope}"
  role_definition_id = azurerm_role_definition.p0_azure_custom_role.role_definition_resource_id
  principal_type     = "ServicePrincipal"

  principal_id = "${clientServicePrincipalId}"
  condition    = <<-EOT
  ${condition}
  EOT
}`,
    },
  ];
  return {
    help: (
      <Typography.Paragraph>
        In order to provision access to your Azure subscription, we need to
        create a custom role and assign it to the Azure App Registration
        you&apos;ve already integrated with P0.
      </Typography.Paragraph>
    ),
    commands: {
      shell,
      iac,
    },
  };
};

export const azureIamWriteInstaller: FrontendInstaller<
  typeof AzureComponents
>["iam-write"] = {
  prerequisiteMessages: appRegistrationPreqMessages,
  instructions: azureIamWriteInstructionsFor,
  items: {},
};
